import { BridgeComponent } from "@hotwired/hotwire-native-bridge"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends BridgeComponent {
  static component = "google-sign-in"
  static targets = ["button", "status"]
  static values = { native: Boolean, callback: String }
  // Load even in old wrappers so they cannot submit embedded Google OAuth.
  static get shouldLoad() { return true }

  connect() {
    super.connect()
    this.abort?.abort()
    this.abort = new AbortController()
    this.busy = false
    this.configured = !this.buttonTarget.disabled
  }

  disconnect() {
    this.cancelPicker()
    this.abort?.abort()
    super.disconnect()
  }

  async submit(event) {
    if (!this.nativeValue) return
    event.preventDefault()
    if (this.busy) return
    if (!this.enabled) {
      this.statusTarget.textContent = "Update the app to use Google sign-in, or use your password."
      return
    }
    this.busy = true
    this.buttonTarget.disabled = true
    this.statusTarget.textContent = "Choose your Google account…"
    const signal = this.abort.signal
    try {
      const fields = Object.fromEntries(new FormData(this.element))
      const challenge = await this.post(this.element.action, fields, signal)
      if (signal.aborted) return
      this.pendingNonce = challenge.nonce
      const credential = await this.pickAccount(challenge, signal)
      const result = await this.post(this.callbackValue, { id_token: credential }, signal)
      if (!signal.aborted) Turbo.visit(result.destination, { action: "replace" })
    } catch (error) {
      if (!signal.aborted) this.statusTarget.textContent = error.message
    } finally {
      this.cancelPicker()
      if (!signal.aborted) {
        this.busy = false
        this.buttonTarget.disabled = !this.configured
      }
      this.removePendingCallbacks()
      this.pendingMessageCallbacks = []
    }
  }

  cancelPicker() {
    if (this.pendingNonce && this.enabled) this.send("cancel", { nonce: this.pendingNonce })
    this.pendingNonce = null
  }

  pickAccount(challenge, signal) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => finish(null, "Google sign-in timed out. Try again."), 120000)
      const aborted = () => finish(null, "Google sign-in was interrupted.")
      let finished = false
      const finish = (token, error) => {
        if (finished) return
        finished = true
        clearTimeout(timer)
        signal.removeEventListener("abort", aborted)
        error ? reject(new Error(error)) : resolve(token)
      }
      signal.addEventListener("abort", aborted, { once: true })
      this.send("sign-in", challenge, message => {
        const token = message.data.idToken
        delete message.data.idToken
        if (signal.aborted) return aborted()
        if (token) finish(token)
        else finish(null, message.data.error === "cancelled" ? "Google sign-in cancelled." : "Google sign-in is unavailable. Try again or use your password.")
      })
    })
  }

  async post(url, body, signal) {
    const target = new URL(url, window.location.href)
    if (target.origin !== window.location.origin) throw new Error("Invalid sign-in destination.")
    const response = await fetch(target, {
      method: "POST", credentials: "same-origin", cache: "no-store", signal,
      headers: {
        "Content-Type": "application/json", "Accept": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
      },
      body: JSON.stringify(body)
    })
    if (!response.ok) throw new Error("Google sign-in could not be completed. Try again, or use your password and link Google from Account.")
    return response.json()
  }
}
